'use client';

import { Dispatch, SetStateAction, useState } from 'react';

import { Alert, AlertTitle } from '@/components/common/feedback/alert/alert';
import { IconEye, IconEyeInvisible } from '@/components/common/icons/SVGs';
import { Button } from '@/components/common/inputs/button/button';
import { Input } from '@/components/common/inputs/text/input';
import { Label } from '@/components/common/inputs/text/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogTitle,
} from '@/components/common/overlay/dialog/dialog';
import { CDN_BASE_URL } from '@/lib/constants/cdn';
import { cognitoService } from '@/lib/services/cognito.service';
import { logger } from '@/lib/utils/logger';
import { useI18n } from '@/locales/client';
import Image from 'next/image';
import { z } from 'zod';

type Props = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  onComplete: () => void;
};

// Define stages of the forgot password flow
type ForgotPasswordStage = 'email' | 'code' | 'success';

// Email validation schema
const isEmailValid = (text: string) =>
  z.string().email().safeParse(text).success;

// Password validation schema
const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters long')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')
  .regex(
    /[^A-Za-z0-9]/,
    'Password must contain at least one special character'
  );

export default function ForgotPasswordModal({
  open,
  setOpen,
  onComplete,
}: Props) {
  const t = useI18n();
  const [stage, setStage] = useState<ForgotPasswordStage>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);

  const resetForm = () => {
    setEmail('');
    setCode('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setStage('email');
    setIsLoading(false);
  };

  const handleRequestCode = async () => {
    setError('');

    if (!email || !isEmailValid(email)) {
      setError('Email is required');
      return;
    }

    setIsLoading(true);
    try {
      await cognitoService.forgotPassword(email);
      setStage('code');
      logger.info('Password reset code requested', { email });
    } catch (err) {
      logger.error('Failed to request password reset code', {
        email,
        error: err,
      });
      // Don't expose whether the email exists or not, for security
      setStage('code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setError('');

    // Validate code
    if (!code || code.trim().length === 0) {
      setError('Verification code is required');
      return;
    }

    // Validate password
    const passwordResult = passwordSchema.safeParse(newPassword);
    if (!passwordResult.success) {
      setError(passwordResult.error.errors[0].message);
      return;
    }

    // Check if passwords match
    if (newPassword !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }

    setIsLoading(true);
    try {
      await cognitoService.confirmForgotPassword(email, code, newPassword);
      setStage('success');
      logger.info('Password reset successful', { email });
    } catch (err: any) {
      if (err.code === 'CodeMismatchException') {
        setError('Invalid verification code');
      } else if (err.code === 'ExpiredCodeException') {
        setError('Verification code has expired');
      } else if (err.code === 'InvalidPasswordException') {
        setError('Password must meet the requirements');
      } else if (
        err.code === 'LimitExceededException' ||
        err.code === 'TooManyRequestsException'
      ) {
        setError('Too many attempts, please try again later');
      } else {
        setError('An error occurred. Please try again.');
      }
      logger.error('Failed to reset password', { email, error: err });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (stage === 'success') {
      onComplete();
    }
    resetForm();
    setOpen(false);
  };

  const renderStage = () => {
    switch (stage) {
      case 'email':
        return (
          <div className='space-y-4'>
            <h2 className='text-xl font-semibold'>
              {t('auth.forgotPassword')}
            </h2>
            <p className='text-sm text-muted-foreground'>
              {t('auth.forgotPasswordModal.description')}
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleRequestCode();
              }}
              className='space-y-2'
            >
              <div className='space-y-2'>
                <Label htmlFor='email'>{t('auth.email')}</Label>
                <Input
                  id='email'
                  type='email'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.forgotPasswordModal.emailStep.title')}
                  className='w-full'
                />
              </div>
              <Button type='submit' className='w-full' disabled={isLoading}>
                {isLoading
                  ? t('common.loading')
                  : t('auth.forgotPasswordModal.emailStep.submit')}
              </Button>
            </form>
          </div>
        );
      case 'code':
        return (
          <div className='space-y-4'>
            <h2 className='text-xl font-semibold'>{t('auth.resetPassword')}</h2>
            <p className='text-sm text-muted-foreground'>
              {t('auth.forgotPasswordModal.emailStep.emailSent')}
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleResetPassword();
              }}
              className='space-y-4'
            >
              <div className='space-y-2'>
                <Label htmlFor='code'>
                  {t('auth.forgotPasswordModal.verificationCode')}
                </Label>
                <Input
                  id='code'
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder={t('auth.forgotPasswordModal.enterCode')}
                  className='w-full'
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='new-password'>
                  {t('auth.forgotPasswordModal.newPassword')}
                </Label>
                <div className='relative'>
                  <Input
                    id='new-password'
                    type={passwordVisible ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={t('auth.forgotPasswordModal.enterNewPassword')}
                    className='w-full pr-10'
                  />
                  <button
                    type='button'
                    onClick={() => setPasswordVisible(!passwordVisible)}
                    className='absolute right-3 top-1/2 -translate-y-1/2'
                  >
                    {passwordVisible ? (
                      <IconEyeInvisible
                        color='currentColor'
                        height={20}
                        width={20}
                      />
                    ) : (
                      <IconEye color='currentColor' height={20} width={20} />
                    )}
                  </button>
                </div>
                <p className='text-xs text-muted-foreground'>
                  {t('auth.forgotPasswordModal.passwordRequirements')}
                </p>
              </div>
              <div className='space-y-2'>
                <Label htmlFor='confirm-password'>
                  {t('auth.forgotPasswordModal.confirmPassword')}
                </Label>
                <Input
                  id='confirm-password'
                  type={passwordVisible ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t(
                    'auth.forgotPasswordModal.enterConfirmPassword'
                  )}
                  className='w-full'
                />
              </div>
              <div className='flex space-x-2'>
                <Button
                  type='button'
                  variant='outline'
                  onClick={() => setStage('email')}
                  disabled={isLoading}
                  className='flex-1'
                >
                  {t('common.back')}
                </Button>
                <Button type='submit' className='flex-1' disabled={isLoading}>
                  {isLoading ? t('common.loading') : t('auth.resetPassword')}
                </Button>
              </div>
            </form>
          </div>
        );
      case 'success':
        return (
          <div className='space-y-4 text-center'>
            <div className='flex justify-center'>
              <div className='rounded-full bg-green-100 p-3'>
                <svg
                  className='h-6 w-6 text-green-600'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M5 13l4 4L19 7'
                  ></path>
                </svg>
              </div>
            </div>
            <h2 className='text-xl font-semibold'>
              {t('auth.forgotPasswordModal.passwordResetSuccess')}
            </h2>
            <p className='text-sm text-muted-foreground'>
              {t('auth.forgotPasswordModal.passwordResetSuccessDescription')}
            </p>
            <Button className='w-full' onClick={handleClose}>
              {t('menu.login')}
            </Button>
          </div>
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogOverlay className='bg-background/80 backdrop-blur-sm' />
      <DialogContent className='w-[368px] rounded-lg border border-border bg-background px-6 py-8'>
        <DialogTitle className='sr-only'>
          {t('auth.forgotPassword')}
        </DialogTitle>
        <DialogDescription className='sr-only'>
          {stage === 'email'
            ? t('auth.forgotPasswordModal.description')
            : stage === 'code'
              ? t('auth.forgotPasswordModal.codeStep.description')
              : t('auth.forgotPasswordModal.passwordResetSuccessDescription')}
        </DialogDescription>
        <div className='flex flex-col items-center space-y-4 sm:w-[325px]'>
          <div className='flex flex-col items-center'>
            <div className='relative h-[111px] w-[111px]'>
              <Image
                fill
                sizes='(max-width: 111px) 100vw, 111px'
                style={{ objectFit: 'cover' }}
                alt='Jaqln Logo'
                priority
                src={`${CDN_BASE_URL}/JAQLNLogo.png`}
              />
            </div>
          </div>
          <div className='w-full'>
            {error && (
              <Alert className='mb-4'>
                <AlertTitle className='text-lg font-bold text-red-500'>
                  Error!
                </AlertTitle>
                <p className='text-xs font-bold text-red-500'>{error}</p>
              </Alert>
            )}
            {renderStage()}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
